import { inject, Injectable } from '@angular/core';
import { Observable, Subject, catchError, debounceTime, filter, map, of, switchMap, tap } from 'rxjs';
import { AdvancedFilter, AdvancedFilterProject } from '../models/advanced-filter';
import { Project } from '../models/project';
import { AdvancedFilterService } from './advanced-filter.service';

export interface DuplicatedCombination {
  key: string;
  projects: AdvancedFilterProject[];
}

interface CombinationRequest {
  project: Project;
  key?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DuplicatedCombinationLogicService {

  private static readonly DEBOUNCE_MS: number = 400;

  private readonly advancedFilterService = inject(AdvancedFilterService);

  private readonly combinationChanged$ = new Subject<Project>();
  private readonly cache = new Map<string, AdvancedFilterProject[]>();

  public readonly duplicatedCombination$: Observable<DuplicatedCombination> =
    this.combinationChanged$.pipe(
      map((project: Project) => ({ project, key: this.buildKey(project) } as CombinationRequest)),
      filter((request: CombinationRequest): request is Required<CombinationRequest> => !!request.key),
      debounceTime(DuplicatedCombinationLogicService.DEBOUNCE_MS),
      switchMap(({ key, project }) => this.findProjects(key, project).pipe(
        map((projects: AdvancedFilterProject[]) => ({ key, projects: this.excludeCurrentProject(projects, project) }))
      )),
      filter((result: DuplicatedCombination) => result.projects.length > 0)
    );

  public notifyCombinationChanged(project?: Project): void {
    if (project) this.combinationChanged$.next(project);
  }

  public reset(): void {
    this.cache.clear();
  }

  public hasCombination(project?: Project): boolean {
    return !!project && !!this.buildKey(project);
  }

  private buildKey(project: Project): string | undefined {
    if (!project.formulationPk) return undefined;
    if (project.regionPk) return `${project.formulationPk}|R${project.regionPk}`;
    if (project.countryPk) return `${project.formulationPk}|C${project.countryPk}`;
    return undefined;
  }

  private findProjects(key: string, project: Project): Observable<AdvancedFilterProject[]> {
    const cached = this.cache.get(key);
    if (cached) return of(cached);

    return this.advancedFilterService.advancedFilter(this.buildFilter(project)).pipe(
      map((projects: AdvancedFilterProject[]) => projects ?? []),
      tap((projects: AdvancedFilterProject[]) => this.cache.set(key, projects)),
      catchError(() => of([] as AdvancedFilterProject[]))
    );
  }

  private buildFilter(project: Project): AdvancedFilter {
    return {
      project: [],
      createdBy: [],
      creationDate: [],
      formulation: [project.formulationPk!],
      parent: [],
      metabolite: [],
      model: [],
      crop: [],
      status: [],
      geography: {
        Regions: project.regionPk ? [project.regionPk] : [],
        Countries: project.countryPk ? [project.countryPk] : []
      },
      archive: false
    };
  }

  private excludeCurrentProject(projects: AdvancedFilterProject[], current: Project): AdvancedFilterProject[] {
    return projects.filter((project: AdvancedFilterProject) => project.projectPk !== current.projectPk);
  }
}
